import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _uniqueId from "lodash/uniqueId";
import _omit from "lodash/omit";
import _isEqual from "lodash/isEqual";
import _get from "lodash/get";
var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1;
import { action, computed, observable, toJS } from 'mobx';
import { showSuccessToast } from 'core/components/toast';
import api from 'core/util/api';
// we never serialize these fields to the API.
const EDITED_CREATED_FIELDS = ['edate', 'cdate'];
/**
 * Standard Model class.
 *
 * @typeParam T - should always be defined and represents the interface for this Model if this Model were a simple JSON object.
 * @typeParam D - `keyof defaults()`
 */
export let Model = (_dec = observable.shallow, _class = class Model {
  get urlPaths() {
    return undefined;
  }
  constructor(attributes) {
    _initializerDefineProperty(this, "error", _descriptor, this);
    this.optimisticId = _uniqueId('i_');
    this.attributes = void 0;
    /**
     * This is listed as public but it's probably best to treat it as protected - accessing collection indirectly like
     * this is pretty funky! Also note that you may need to cast this `as unknown as MyCollection` when you want to use
     * it externally.
     */
    // eslint-disable-next-line no-use-before-define
    this.collection = null;
    _initializerDefineProperty(this, "requestStatus", _descriptor2, this);
    _initializerDefineProperty(this, "requestProgress", _descriptor3, this);
    _initializerDefineProperty(this, "lastUpdated", _descriptor4, this);
    /**
     * Do not read this from outside of Collection/Model!
     */
    this.store = void 0;
    this.fetchMethod = void 0;
    /**
     * Questions whether the request exists and matches a certain label.
     * If no label are passed it will check if any requests are active.
     */
    this.isRequestActive = label => {
      if (!label) {
        return this.requestStatus !== null;
      }
      return !!this.requestStatus && this.requestStatus === label || !!this.collection && this.collection.isRequestActive(label);
    };
    _initializerDefineProperty(this, "setRequestStatus", _descriptor5, this);
    /**
     * Set the current selected model in a collection to this model. Typically this
     * method is called from a UI with a direct reference to `this`, so we keep the
     * binding with an arrow function. `onClick={model.select}`
     */
    _initializerDefineProperty(this, "select", _descriptor6, this);
    _initializerDefineProperty(this, "deselect", _descriptor7, this);
    _initializerDefineProperty(this, "setLastUpdated", _descriptor8, this);
    /**
     * Replaces all of the attributes with new ones.
     */
    _initializerDefineProperty(this, "replace", _descriptor9, this);
    _initializerDefineProperty(this, "duplicate", _descriptor0, this);
    _initializerDefineProperty(this, "destroyModel", _descriptor1, this);
    /*
     * Very important to note that any fields used here, must be defined with getter syntax
     * in subclasses or they will be undefined here!
     */

    this.attributes = observable.object(Object.assign({}, this.defaults, attributes), {}, {
      deep: false,
      name: 'Model.attributes'
    });
  }
  get defaults() {
    return {};
  }

  /**
   * Return the base url used in the `url` method
   */
  get urlRoot() {
    return undefined;
  }
  get omitDuringSerialize() {
    return [];
  }
  get messages() {
    return {
      create: 'Added successfully',
      update: 'Updated successfully',
      destroy: 'Removed successfully',
      duplicate: 'Duplicated successfully'
    };
  }
  get showToastTitles() {
    return false;
  }
  get index() {
    if (!this.collection) {
      throw new Error('index: Model is not a part of a collection');
    }
    return this.collection.models.indexOf(this);
  }

  /**
   * Return the url for this given REST resource
   */
  get url() {
    if (!this.urlRoot && (!this.collection || !this.collection.url)) {
      throw new Error('No `urlRoot` provided');
    }
    const urlRoot = this.urlRoot ?? this.collection?.url;
    if (!urlRoot) {
      // TODO should ideally be a compile-time error
      throw new Error('Model fetch attempted for model with no defined URL!');
    }
    if (this.isNew) {
      return urlRoot;
    }
    return `${urlRoot}/${this.get('id')}`;
  }
  toDate(_dateType) {
    return undefined;
  }
  get lastEdited() {
    return this.toDate('etime') || this.toDate('edate');
  }
  get created() {
    return this.toDate('ctime') || this.toDate('cdate');
  }

  /**
   * Whether the resource is new or not
   *
   * We determine this asking if it contains the `id` attribute (set by the server).
   */
  get isNew() {
    return !this.has('id') || !this.get('id') && this.get('id') !== 0;
  }

  /**
   * Determines whether or not this Model is selectable in it's collection.
   */
  get isSelectable() {
    return true;
  }

  /**
   * Determines whether or not this Model is selected in it's collection.
   */
  get isSelected() {
    if (!this.collection) {
      console.warn(`BaseModel (${this.constructor.name}) is not a part of a Collection. Cannot call isSelected()`);
      return false;
    }
    const {
      selected
    } = this.collection;
    if (Array.isArray(selected)) {
      return selected.includes(this);
    }
    return this.collection.selected === this;
  }

  /**
   * Get an id from the model. It will use either
   * the backend assigned one or the client.
   */
  get id() {
    if (this.has('id')) {
      // probably this is the only line we need but I've kept this in for possible legacy issues
      return this.get('id') ?? this.optimisticId;
    }
    return this.optimisticId;
  }
  baseGet(attribute) {
    if (!attribute) {
      return toJS(this.attributes);
    }
    if (typeof attribute === 'string' && attribute.includes('.')) {
      const [r, ...rest] = attribute.split('.');
      const root = r;
      // ignore dot in keys attributes like `f_hll(inet_src_addr,0.0001)__k_last`
      if (!this.attributes[root] && this.has(attribute)) {
        return this.attributes[attribute];
      }
      if (rest.length === 1) {
        // @ts-expect-error left in for legacy purposes but please do not use this - TS won't let you do it safely!
        return this.attributes[root] && this.attributes[root][rest[0]];
      }
      return this.attributes[root] && _get(this.attributes[root], rest.join('.'));
    }
    if (Array.isArray(attribute)) {
      return attribute.reduce((result, attr) => {
        if (attr.includes('.')) {
          [attr] = attr.split('.');
        }
        result[attr] = this.attributes[attr];
        return result;
      }, {});
    }
    return this.attributes[attribute];
  }
  /**
   * Get the attribute from the model.
   *
   * For optional fields use `has()` to check whether the field is populated.
   * If no attribute is passed, returns all of the model attributes as a plain object.
   */
  get(attribute, defaultValue) {
    if (!attribute) {
      return this.baseGet() || defaultValue;
    }
    const result = this.baseGet(attribute);
    return result === undefined ? defaultValue : result;
  }

  /**
   * Returns whether the given field exists for the model.
   */
  has(attribute) {
    return this.attributes[attribute] !== undefined && this.attributes[attribute] !== null;
  }

  // TODO unfortunately it's really hard to get this to work elegantly with correct types! particularly when working with legacy JS code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get sortValues() {
    return {};
  }
  getSortValue(field) {
    if (!field) {
      return '';
    }
    let value;
    if (this.sortValues[field]) {
      value = this.sortValues[field](this);
    } else {
      value = this.get(field);
      if (value === undefined) {
        value = this[field];
      }
    }

    // Replacing undefined with empty string makes _.orderBy happy when ordering numbers and strings
    return typeof value === 'number' ? value : value?.toString() || '';
  }

  /**
   * Returns a raw JS instance of our Model, used for debugging purposes mostly.
   */
  toJS() {
    return toJS(this.attributes);
  }
  isEqual(otherModel) {
    return otherModel && otherModel.toJS && _isEqual(this.toJS(), otherModel.toJS());
  }
  /**
   * Merge the given data with the current attributes.
   *
   * Does NOT merge `undefined` values as this can break type safety! Unfortunately there
   * is not an easy way to define a Partial type which does not allow "defined undefined" values.
   *
   * One day we might want to convert the regular `set()` method to follow this pattern but if so
   * then we can just do a find & replace.
   */
  safeSet(data) {
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        this.attributes[key] = value;
      }
    }
    this.setLastUpdated();
    return this;
  }

  /**
   * Merge the given data with the current attributes
   * @deprecated see `this.safeSet(data)`
   */

  /**
   * Merge the given data with the current attributes
   */
  set(dataOrFieldName = {}, fieldData) {
    if (typeof dataOrFieldName === 'string' && fieldData != null) {
      this.attributes[dataOrFieldName] = fieldData;
    } else {
      Object.assign(this.attributes, dataOrFieldName);
    }
    this.setLastUpdated();
    return this;
  }
  /**
   * Saves the resource on the backend.
   *
   * If the item has an `id` it updates it, otherwise it creates the new resource.
   *
   * It supports optimistic and patch updates.
   *
   * @param attributes
   * @param options
   * @param options.optimistic - updates the model before we get a response back from the server.
   * @param options.patch - uses `PATCH` versus `PUT`.
   */
  async save(attributes, options = {}) {
    const {
      optimistic = false,
      patch = false,
      preserialize = true,
      setOnPatchSuccess = true,
      clearSelection = true,
      toast = true,
      toastMessage,
      sendProvidedValuesOnly = false,
      url
    } = options;
    this.requestStatus = 'updating';
    this.requestProgress = 0;
    if (this.isNew) {
      this.set(Object.assign({}, attributes));
      return this.create(attributes, {
        optimistic,
        toast,
        clearSelection,
        url
      });
    }
    let data = attributes;
    const originalAttributes = this.get();

    // if PATCH, we only want to send the values that changed.
    if (patch) {
      data = Object.keys(attributes).reduce((result, att) => {
        const attribute = att;
        const newValue = attributes[attribute];
        const oldValue = originalAttributes[attribute];
        if (newValue !== oldValue) {
          // @ts-expect-error TODO this is pretty unsafe and can result in accidental data deletion! but also very hard to rewrite
          result[attribute] = newValue;
        }
        return result;
      }, {});
    } else if (!sendProvidedValuesOnly) {
      data = Object.assign({}, originalAttributes, attributes);
    }
    if (optimistic) {
      this.set(attributes);
    }
    const saveUrl = url || (this.urlPaths ? this.urlPaths.update : this.url);
    const apiMethod = patch ? 'patch' : 'put';
    const promise = api[apiMethod](saveUrl, {
      data: preserialize ? this.serialize(data) : data
    });
    return promise.then(success => {
      if (this.collection && clearSelection) {
        this.collection.clearSelection();
      }
      if (!patch || setOnPatchSuccess) {
        this.set(this.deserialize(success));
      }
      if (toast) {
        showSuccessToast(toastMessage || this.messages.update, {
          title: this.showToastTitles ? 'Updated' : undefined
        });
      }
      if (this.collection) {
        this.collection.setLastUpdated();
      }
      this.setLastUpdated();
      this.requestStatus = null;
      return success;
    }, error => {
      if (optimistic) {
        this.set(originalAttributes);
      }
      this.error = {
        label: 'updating'
      };
      this.requestStatus = null;
      throw error;
    });
  }

  /**
   * Saves this new model on the backend (via POST operation)
   */
  async create(attributes = {}, options = {}) {
    const label = 'creating';
    const data = Object.assign({}, this.get(), attributes);
    const {
      clearSelection,
      toast = true
    } = options;
    const url = options.url || (this.collection && this.collection.url ? this.collection.url : this.urlRoot);
    if (!url) {
      throw new Error('Attempted to save Model with no defined URL');
    }
    const payload = this.serialize(data);
    const promise = api.post(url, {
      data: payload
    });
    this.requestStatus = label;
    return promise.then(success => {
      this.set(this.deserialize(success));
      if (this.collection) {
        this.collection.add([this]);
        if (clearSelection) {
          this.collection.clearSelection();
          this.collection.sort(); // Note: it seemed to make sense not to re-apply existing filter rules
        }
      }
      if (toast) {
        const createMessage = this.messages ? this.messages.create : 'Created Successfully';
        showSuccessToast(createMessage, {
          title: this.showToastTitles ? 'Created' : undefined
        });
      }
      this.requestStatus = null;
      return success;
    }, error => {
      this.error = {
        label,
        body: error
      };
      this.requestStatus = null;
      throw error;
    });
  }
  async fetch(options = {}) {
    const label = 'fetching';
    this.requestStatus = label;
    this.requestProgress = 0;
    const url = this.urlPaths ? this.urlPaths.fetch : this.url;
    const apiFetchMethod = this.fetchMethod === 'post' ? api.post : api.get;
    const promise = apiFetchMethod(url, options);
    return promise.then(success => {
      this.set(this.deserialize(success));
      this.setRequestStatus(null);
      return success;
    }, error => {
      this.error = {
        label,
        body: error
      };
      this.requestStatus = null;
      throw error;
    });
  }
  /**
   * Destroys the resource on the client and requests the backend to delete it there too
   */
  async destroy(options = {}) {
    if (this.collection) {
      if (this.isNew || !this.urlRoot) {
        return Promise.resolve(this.collection.remove([this.id.toString()]));
      }
    }
    return this.destroyModel(options);
  }
  serialize(data) {
    return _omit(data || this.get(), [...this.omitDuringSerialize, ...EDITED_CREATED_FIELDS]);
  }
  get jsonRoot() {
    return undefined;
  }

  /**
   * @returns Preferably this would return `WithOptional<T,D>` however this is left up to the implementation. The default is to return `Partial<T>`.
   */
  deserialize(data) {
    return data && this.jsonRoot ? data[this.jsonRoot] : data;
  }
  getUniqueCopyName(nameField) {
    let name = this.get(nameField)?.toString() || '';
    if (this.collection) {
      // Remove the [Copy] or [Copy x] from the name
      const nameMatch = name.match(/\[Copy.*\] (.+)/);
      if (nameMatch) {
        name = nameMatch[nameMatch.length - 1];
      }
      if (!this.collection.find({
        [nameField]: `[Copy] ${name}`
      })) {
        return `[Copy] ${name}`;
      }
      for (let i = 1;; i += 1) {
        if (!this.collection.find({
          [nameField]: `[Copy ${i}] ${name}`
        })) {
          return `[Copy ${i}] ${name}`;
        }
      }
    }
    return name;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "error", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "requestStatus", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "requestProgress", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "lastUpdated", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "index", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "index"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "lastEdited", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "lastEdited"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "created", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "created"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isNew", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isNew"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isSelectable", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isSelectable"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isSelected", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isSelected"), _class.prototype), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "setRequestStatus", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return status => {
      this.requestStatus = status;
    };
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "select", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (options = {}) => {
      const {
        multi = false,
        shiftKey = false,
        fetch = false
      } = options;
      if (!this.collection) {
        console.warn(`BaseModel (${this.constructor.name}) is not a part of a Collection. Cannot call select()`);
      }
      if (!this.isSelectable) {
        return Promise.resolve();
      }
      if (fetch) {
        return this.fetch().then(() => {
          this.collection?.select(this, {
            multi
          });
        });
      }
      return Promise.resolve().then(() => this.collection?.select(this, {
        multi,
        shiftKey
      }));
    };
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "deselect", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      if (!this.collection) {
        console.warn(`Model (${this.constructor.name}: ${this.id}) is not a part of a Collection. Cannot call select()`);
      }
      if (!this.isSelected) {
        console.warn(`Model (${this.constructor.name}: ${this.id}) is not selected.`);
      }
      this.collection?.clearSelection();
    };
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "setLastUpdated", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.lastUpdated = Date.now();
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "safeSet", [action], Object.getOwnPropertyDescriptor(_class.prototype, "safeSet"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "set", [action], Object.getOwnPropertyDescriptor(_class.prototype, "set"), _class.prototype), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "replace", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (data = {}) => {
      // @ts-expect-error difficult to get typing in here right now, needs reworking
      this.attributes = observable.object(data, {}, {
        deep: false,
        name: 'Model.attributes'
      });
      this.setLastUpdated();
    };
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "duplicate", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (options = {}) => {
      const {
        removeId = true,
        save = true,
        deserialize = true
      } = options;
      const attributes = removeId ? _omit(this.get(), ['id']) : this.get();
      if (!this.collection) {
        const Constructor = this.constructor;
        return new Constructor(attributes);
      }
      const dupe = this.collection.build(attributes, {
        deserialize
      });
      if (save) {
        dupe.save(attributes);
      }
      return dupe;
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "save", [action], Object.getOwnPropertyDescriptor(_class.prototype, "save"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "create", [action], Object.getOwnPropertyDescriptor(_class.prototype, "create"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetch"), _class.prototype), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "destroyModel", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async (options = {}) => {
      const {
        toast = true,
        remove = true,
        url,
        data
      } = options;
      const destroyUrl = url || (this.collection?.urlPaths?.destroy ? `${this.collection.urlPaths.destroy}/${this.get('id')}` : this.url);
      this.requestStatus = 'destroying';
      return api.del(destroyUrl, data ? {
        data
      } : undefined).then(action(success => {
        if (toast) {
          showSuccessToast(this.messages.destroy, {
            title: this.showToastTitles ? 'Removed' : undefined
          });
        }
        if (this.collection) {
          if (remove) {
            this.collection.remove([this.id.toString()]);
          }
          this.collection.clearSelection();
        }
        this.requestStatus = null;
        return success;
      }), action(error => {
        console.error('destroyModel error', error);
        if (options.optimistic && this.collection) {
          this.collection.add([this.get()]);
        }
        this.error = {
          label: 'destroying',
          body: error
        };
        this.requestStatus = null;
        return error;
      }));
    };
  }
}), _class);

/**
 * A concrete, non-generic version of the Model class that can be
 * easily instantiated from JavaScript files.
 *
 * @deprecated for future code it is generally preferable to create your own type-safe Model
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default class GenericModel extends Model {}